<template>
  <section
    v-if="widget && widget.available"
    class="widget grid-stack-item-content"
    :class="{ 'widget-edit': editMode, 'premium-metric': widget.is_premium }"
  >
    <div class="widget__header">
      <IconBar />
      <div class="widget__header-title">
        <span>{{ widget.name }}</span>
        <Tooltip v-if="widget.is_premium" position="top">
          <template v-slot:text>
            <IconPremium class="premium-icon" />
          </template>
          <template v-slot:tooltip>
            Premium Feature
          </template>
        </Tooltip>
      </div>
      <Tooltip position="left">
        <template v-slot:text>
          <IconInfo />
        </template>
        <template v-slot:tooltip>
          {{ widgetTooltipText() }}
        </template>
      </Tooltip>
      <ul class="widget__header-controls">
        <li
          v-for="(area, index) in widget.filters.area"
          :key="index"
          @click="changeArea(area)"
          class="widget__header-control"
          :class="{ 'widget__header-control--active': chosenArea?.name === area.name }"
        >
          {{ area.name }}
        </li>
      </ul>
    </div>

    <div class="widget__subheader">
      <div
        v-if="showZoneSelection"
        class="widget__subheader-controls widget__subheader-controls__zones"
      >
        <div class="widget__label">Zone</div>
        <Select v-model="chosenZone" :options="chosenArea?.data" optionLabel="title" />
      </div>
      <div
        v-if="showClusterSelection"
        class="widget__subheader-controls widget__subheader-controls__zones"
      >
        <div class="widget__label">Cluster</div>
        <Select v-model="chosenCluster" :options="chosenArea?.data" optionLabel="title" />
      </div>
      <div
        v-if="showDaySelection"
        class="widget__subheader-controls widget__subheader-controls__zones"
      >
        <div class="widget__label">Day</div>
        <Select v-model="chosenDay" :options="chosenTime?.data" optionLabel="title" />
      </div>

      <ul class="widget__subheader-controls">
        <li
          v-for="(time, index) in widget.filters.time"
          :key="index"
          @click="changeTime(time)"
          class="widget__subheader-control"
          :class="{ 'widget__header-control--active': chosenTime?.name === time.name }"
        >
          {{ time.name }}
        </li>
      </ul>
    </div>

    <div class="widget__body-wrapper">
      <div class="widget__body">
        <div v-if="widget.data.length === 0 && !widget.is_premium" class="widget__body-empty">
          <IconInfo />
          <p>No data available</p>
        </div>
        <!-- Average Visits -->
        <!-- Average Repeats -->
        <AveragesDisplay
          v-else-if="widget.code_name === 'AverageVisits' || widget.code_name === 'AverageRepeats'"
          :widget="widget"
          :area="chosenArea"
          :time="chosenTime"
          :zone="chosenZone"
          :cluster="chosenCluster"
        />

        <!-- Unique Visitors -->
        <UniqueVisitors
          v-else-if="widget.code_name === 'UniqueVisitors'"
          :widget="widget"
          :area="chosenArea"
          :time="chosenTime"
          :zone="chosenZone"
          :cluster="chosenCluster"
          :day="chosenDay"
        />

        <!-- Zones Visited -->
        <ZonesVisited
          v-else-if="widget.code_name === 'ZonesVisited'"
          :widget="widget"
          :area="chosenArea"
          :time="chosenTime"
          :zone="chosenZone"
          :cluster="chosenCluster"
        />

        <!-- Days Visited -->
        <DaysVisited v-else-if="widget.code_name === 'DaysVisited'" :widget="widget" />

        <!-- Visits -->
        <Visits
          v-else-if="widget.code_name === 'Visits'"
          :widget="widget"
          :area="chosenArea"
          :time="chosenTime"
          :zone="chosenZone"
          :cluster="chosenCluster"
        />

        <!-- Repeat Visits -->
        <RepeatVisits
          v-else-if="widget.code_name === 'RepeatVisits'"
          :widget="widget"
          :area="chosenArea"
          :time="chosenTime"
          :zone="chosenZone"
          :cluster="chosenCluster"
        />

        <RepeatVisitors
          v-else-if="widget.code_name === 'RepeatVisitors'"
          :widget="widget"
          :area="chosenArea"
          :cluster="chosenCluster"
        />

        <!-- Visit Dwell -->
        <VisitorDwell
          v-else-if="widget.code_name === 'EngagedVisitorDwell'"
          :widget="widget"
          :area="chosenArea"
          :time="chosenTime"
          :zone="chosenZone"
          :cluster="chosenCluster"
        />

        <!-- Visit Dwell -->
        <VisitDwell
          v-else-if="widget.code_name === 'EngagedVisitDwell'"
          :widget="widget"
          :area="chosenArea"
          :time="chosenTime"
          :zone="chosenZone"
          :day="chosenDay"
        />

        <!-- Distributed  Dwell -->
        <DistributedDwell
          v-else-if="widget.code_name === 'DistributedDwell'"
          :widget="widget"
          :area="chosenArea"
          :time="chosenTime"
          :zone="chosenZone"
          :day="chosenDay"
        />

        <!-- Visitors by Day -->
        <VisitorDay
          v-else-if="widget.code_name === 'VisitorsByDay'"
          :widget="widget"
          :area="chosenArea"
          :time="chosenTime"
          :zone="chosenZone"
          :day="chosenDay"
        />

        <!-- Benchmark Engaged Repeat Visits -->
        <BenchmarkEngagedRepeatVisits
          v-else-if="widget.code_name === 'BenchmarkEngagedRepeatVisits'"
          :widget="widget"
          :area="chosenArea"
          :zone="chosenZone"
        />

        <!-- Benchmark Conversion Rate -->
        <BenchmarkConversionRate
          v-else-if="widget.code_name === 'BenchmarkConversionRate'"
          :widget="widget"
          :area="chosenArea"
          :zone="chosenZone"
        />

        <!-- Benchmark Engaged Dwell Time -->
        <BenchmarkEngagedDwellTime
          v-else-if="widget.code_name === 'BenchmarkEngagedDwellTime'"
          :widget="widget"
          :area="chosenArea"
          :zone="chosenZone"
        />

        <!-- Premium Distributed Dwell -->
        <TotalEngagedDwellByZone
          v-else-if="widget.code_name === 'PremiumTotalEngagedDwellByZone'"
          :widget="widget"
          :area="chosenArea"
          :zone="chosenZone"
        />

        <!-- Cumulative Arrival -->
        <CumulativeArrival
          v-else-if="widget.code_name === 'PremiumCumulativeArrival'"
          :widget="widget"
          :area="chosenArea"
          :zone="chosenZone"
        />

        <ZoneOverlap
          v-else-if="widget.code_name === 'PremiumZoneOverlap'"
          :widget="widget"
          :area="chosenArea"
          :zone="chosenZone"
        />

        <MostCommonPath
          v-else-if="widget.code_name === 'PremiumMostCommonPath'"
          :widget="widget"
          :area="chosenArea"
          :zone="chosenZone"
        />

        <!-- Premium Metrics -->
        <ArrivalsByDay
          v-else-if="widget.code_name === 'PremiumArrivalsByDay'"
          :widget="widget"
          :area="chosenArea"
          :zone="chosenZone"
        />
      </div>
    </div>

    <div class="widget__footer">
      <div class="widget__footer-controls" v-show="editMode">
        <div class="widget-edit-controls">
          <span class="widget-control widget-control--move" aria-label="Move widget">
            <IconMove />
          </span>
          <span class="widget-control widget-control--remove">
            <Button outlined @click="removeWidget" aria-label="Remove widget">
              <IconRemove />
            </Button>
          </span>
          <span class="widget-control widget-control--resize" aria-label="Resize widget">
            <!-- Functionality handled by plugin -->
            <IconResize />
          </span>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import mixpanel from '@/mixpanel.ts';

import { Button, Select } from 'primevue';

import Tooltip from '../global/Tooltip.vue';

import type { WidgetFilter, WidgetSetting } from '@/types/Widget';

import ArrivalsByDay from './premium-metrics/ArrivalsByDay.vue';
import AveragesDisplay from './averages/AveragesDisplay.vue';
import BenchmarkEngagedRepeatVisits from './benchmarking/EngagedRepeatVisits.vue';
import BenchmarkConversionRate from './benchmarking/ConversionRate.vue';
import BenchmarkEngagedDwellTime from './benchmarking/EngagedDwellTime.vue';
import CumulativeArrival from './premium-metrics/CumulativeArrival.vue';
import DaysVisited from './days-visited/DaysVisited.vue';
import DistributedDwell from './distributed-dwell/DistributedDwell.vue';
import TotalEngagedDwellByZone from './premium-metrics/TotalEngagedDwellByZone.vue';
import MostCommonPath from './premium-metrics/MostCommonPath.vue';
import RepeatVisits from './repeat-visits/RepeatVisits.vue';
import RepeatVisitors from './repeat-visitors/RepeatVisitors.vue';
import UniqueVisitors from './unique-visitors/UniqueVisitors.vue';
import Visits from './visits/Visits.vue';
import VisitDwell from './visit-dwell/VisitDwell.vue';
import VisitorDay from './visitor-day/VisitorDay.vue';
import VisitorDwell from './visitor-dwell/VisitorDwell.vue';
import ZoneOverlap from './premium-metrics/ZoneOverlap.vue';
import ZonesVisited from './zones-visited/ZonesVisited.vue';

import IconBar from '@/icons/analytics/IconBar.svg?component';
import IconInfo from '@/icons/IconInfo.svg?component';
import IconMove from '@/icons/widgets/IconMove.svg?component';
import IconRemove from '@/icons/IconRemove.svg?component';
import IconResize from '@/icons/widgets/IconResize.svg?component';
import IconPremium from '@/icons/IconPremium.svg?component';

const widget = defineModel<any>('widget');

defineProps({
  editMode: Boolean,
});

const emit = defineEmits(['onWidgetRemove']);

const chosenArea = ref<WidgetFilter>();
const chosenTime = ref<WidgetFilter>();
const chosenZone = ref<WidgetSetting>();
const chosenDay = ref<WidgetSetting>();
const chosenCluster = ref<WidgetSetting>();

// Set Defaults
if (widget.value && widget.value.filters) {
  if (widget.value.filters.area) {
    chosenArea.value = widget.value.filters.area[0];

    const zoneFilter = widget.value.filters.area.find((area: WidgetFilter) => {
      return area.name == 'Zone';
    });

    if (zoneFilter && zoneFilter.data) {
      chosenZone.value = zoneFilter.data[0];
    }

    const clusterFilter = widget.value.filters.area.find((area: WidgetFilter) => {
      return area.name == 'Cluster';
    });

    if (clusterFilter && clusterFilter.data) {
      chosenCluster.value = clusterFilter.data[0];
    }
  }

  if (widget.value.filters.time) {
    chosenTime.value = widget.value.filters.time[0];

    const dailyFilter = widget.value.filters.time.find((time: WidgetFilter) => {
      return ['Day', 'Hour', '15 Min'].includes(time.name);
    });

    if (dailyFilter && dailyFilter.data) {
      chosenDay.value = dailyFilter.data[0];
    }
  }
}

const changeArea = (area: WidgetFilter) => {
  chosenArea.value = area;
  mixpanel.track('Widget Interaction', {
    Widget: widget.value?.code_name,
    Action: 'Filter Change',
    'Filter Type': 'Area',
  });
};

const changeTime = (time: WidgetFilter) => {
  const day = chosenDay.value?.id;

  chosenTime.value = time;

  // If we change the time but there is a chosenDay
  // that still exists, let's keep using it.
  if (day && chosenTime.value) {
    const equalDay = chosenTime.value.data?.find((time) => {
      return time.id === day;
    });

    chosenDay.value = equalDay ? equalDay : time.data ? time.data[0] : undefined;
  } else {
    chosenDay.value = time.data ? time.data[0] : undefined;
  }
  mixpanel.track('Widget Interaction', {
    Widget: widget.value?.code_name,
    Action: 'Filter Change',
    'Filter Type': 'Time',
  });
};

const showDaySelection = computed(() => {
  const isDaily = chosenTime.value?.name === 'Day';

  if (isDaily) {
    const isDailyZone = chosenArea.value?.name === 'Zone Average';
    const isVisitorDistribution = widget.value?.code_name === 'VisitorsByDay';
    const isDistributedDwell = widget.value?.code_name === 'DistributedDwell';

    return isDailyZone || isVisitorDistribution || isDistributedDwell;
  }

  const isUnique = widget.value?.code_name === 'UniqueVisitors';
  return isUnique && chosenTime.value?.name != 'Total';
});

const showZoneSelection = computed(() => {
  const isZoneArea = chosenArea.value?.name === 'Zone';
  const isTotal = chosenTime.value?.name === 'Total';

  if (widget.value?.code_name === 'UniqueVisitors') {
    return isZoneArea && !isTotal;
  }

  return isZoneArea;
});

const showClusterSelection = computed(() => {
  const isClusterArea = chosenArea.value?.name === 'Cluster';
  const isTotal = chosenTime.value?.name === 'Total';

  if (widget.value?.code_name === 'UniqueVisitors') {
    return isClusterArea && !isTotal;
  }

  return isClusterArea;
});

watch(chosenZone, (newZone) => {
  if (newZone) {
    mixpanel.track('Widget Interaction', {
      widget: widget.value?.code_name,
      action: 'Filter Change',
      filterType: 'Zone',
    });
  }
});

watch(chosenCluster, (newCluster) => {
  if (newCluster) {
    mixpanel.track('Widget Interaction', {
      widget: widget.value?.code_name,
      action: 'Filter Change',
      filterType: 'Cluster',
    });
  }
});

watch(chosenDay, (newDay) => {
  if (newDay) {
    mixpanel.track('Widget Interaction', {
      widget: widget.value?.code_name,
      action: 'Filter Change',
      filterType: 'Day',
    });
  }
});

const widgetTooltipText = () => {
  let text = '';

  switch (widget.value?.code_name) {
    case 'Visits':
      text =
        'Shows the visits made by engaged visitors at the Site and Zone levels for the entire duration of the event.';
      break;
    case 'RepeatVisits':
      text = 'Visits that were made beyond the first for each visitor.';
      break;
    case 'RepeatVisitors':
      text = 'Visitors who made more than a single visit.';
      break;
    case 'VisitorsByDay':
      text = 'Unique visitors by day.';
      break;
    case 'DistributedDwell':
      text = 'Distribution of counts of visits by their duration.';
      break;
    case 'EngagedVisitorDwell':
      text = 'Average total dwell times for visitors who were engaged.';
      break;
    case 'EngagedVisitDwell':
      text = 'Average dwell times for individual visits which were engaged.';
      break;
    case 'UniqueVisitors':
      text =
        'Distinct detected visitors. Conversion rate measures the percentage of Opportunity To See (OTS) visits that became engaged.';
      break;
    case 'AverageVisits':
      text =
        'Average number of total visits per visitor across the entire event. For example, if a visitor makes 5 visits (3 at zone 1 and 2 at zone 2), they contribute 5 to this average.';
      break;
    case 'AverageRepeats':
      text =
        'Average number of repeat visits per visitor at the Site and Cluster levels. For example, if a visitor makes 7 visits (4 at zone 1 and 3 at zone 2), they contribute 3 to this average.';
      break;
    case 'ZonesVisited':
      text = 'The split of total engaged visits across all zones.';
      break;
    case 'DaysVisited':
      text = 'How many different days visitors were seen on.';
      break;
    case 'BenchmarkEngagedRepeatVisits':
      text =
        'The sum of all repeat visits visitors made to a zone within the engaged range, per day. E.g. if a visitor engages at a zone 3 times, they will contribute 2 visits to this metric.';
      break;
    case 'BenchmarkConversionRate':
      text = 'The percentage of visitors who became engaged with a zone.';
      break;
    case 'BenchmarkEngagedDwellTime':
      text = 'The sum of all dwell time visitors spent engaged at a zone, over all visits.';
      break;
    case 'OnsiteDwellTime':
      text = 'Average time visitors spent on site, measured from first seen to last seen.';
      break;
    case 'PremiumCumulativeArrival':
      text =
        'The percentage of visitors first seen on site, out of all visitors seen on site. Indicative of arrivals.';
      break;
    case 'PremiumArrivalsByDay':
      text = 'The time of day visitors were first seen on site.';
      break;
    case 'PremiumMostCommonPath':
      text = 'The most popular orders of zone visits across the event.';
      break;
    case 'PremiumMultiDayPercentage':
      text = 'Visitors who were seen on more than 1 day, as a percentage of total visitors.';
      break;
    case 'PremiumMultiZoneOverlap':
      text = 'Visitors who were seen at a combination of all zones.';
      break;
    case 'PremiumTrafficOverTime':
      text = 'Total visitors seen at given time intervals';
      break;
    case 'PremiumFirstVisitBySensor':
      text = 'The breakdown percentage and count of visits for each zone.';
      break;
    case 'PremiumZoneOverlap':
      text =
        'Visitors seen at one zone A (rows) that were also seen at another zone B (columns), as a percentage of all visitors to that zone A. Ordered Overlap only counts visitors that were later seen at the second zone B.';
      break;
    case 'PremiumTotalEngagedDwellByZone':
      text =
        'The distribution of time visitors spent engaged across the event broken down by zone.';
      break;
    default:
      text = '';
      break;
  }

  return text;
};

const removeWidget = () => {
  emit('onWidgetRemove', widget.value?.id);
};
</script>

<style lang="scss">
.widget {
  @include sizing.padding('xxs');
  background: variables.$vantage-base-primitives-color-background-100;
  border-radius: variables.$vantage-base-sizing-xs;
  position: relative;

  &.premium-metric {
    border: 1px solid variables.$vantage-base-primitives-color-secondary-400;
  }

  .widget__header {
    @include mixins.flex-center($horiz: flex-start, $wrap: true);
    border-bottom: 1px solid variables.$vantage-base-primitives-color-border-200;
    margin-bottom: variables.$vantage-base-sizing-xs;
    padding-bottom: variables.$vantage-base-sizing-xs;

    span {
      @include sizing.margin(0, 'xxxs');
    }
  }

  .widget__subheader {
    @include mixins.flex-center(
      $horiz: flex-start,
      $gap: variables.$vantage-base-sizing-xs,
      $wrap: true
    );
    margin-bottom: variables.$vantage-base-sizing-xs;
  }

  .widget__header-text {
    @include sizing.padding('xs');
    @include typography.font-body($size: sm, $weight: normal);
    text-align: center;

    span:not(.widget-message__yellow) {
      font-weight: bold;
    }
  }

  .widget__bignumber {
    @include typography.font-display($size: lg, $weight: bold);
    color: variables.$vantage-base-primitives-color-tertiary-500;
    line-height: variables.$vantage-base-sizing-xxxl;
    text-align: center;
  }

  .widget__label {
    @include mixins.flex-center();
    @include sizing.padding(0, 'xl');
    @include typography.font-body($size: sm, $weight: bold);
    color: variables.$vantage-base-primitives-color-tertiary-400;
    text-align: center;
    text-transform: uppercase;

    &.widget__label-thin {
      @include typography.font-body($size: sm, $weight: medium);
      display: block;

      span:not(.widget-message__yellow) {
        font-weight: bold;
      }
    }
  }
}

.widget__body-wrapper {
  align-items: center;
  display: flex;
  height: calc(100% - 130px);
  margin: auto 0;
}

.widget__body {
  height: 100%;
  width: 100%;
}

.widget__body-empty {
  @include mixins.flex-center();
  flex-direction: column;
  @include typography.font-body($size: sm, $weight: normal);
  height: 100%;
  text-align: center;
  color: variables.$vantage-base-primitives-color-tertiary-400;
  width: 100%;
}

.widget__header-controls {
  @include mixins.flex-center($horiz: flex-start);
  list-style-type: none;
  margin-left: auto;
  padding: 0;

  &.widget__header-controls__zones {
    margin-left: 0;

    .p-select {
      margin-left: variables.$vantage-base-sizing-xxs;
    }
  }

  li {
    @include sizing.padding('xxxs', 'xs');
    background: variables.$vantage-base-primitives-color-background-500;
    border: 1px solid variables.$vantage-base-primitives-color-border-100;
    border-right: 1px solid variables.$vantage-base-primitives-color-border-100;
    cursor: pointer;
  }

  li:first-of-type {
    border-top-left-radius: variables.$vantage-base-sizing-xxs;
    border-bottom-left-radius: variables.$vantage-base-sizing-xxs;
  }

  li:last-of-type {
    border-color: variables.$vantage-base-primitives-color-border-100;
    border-top-right-radius: variables.$vantage-base-sizing-xxs;
    border-bottom-right-radius: variables.$vantage-base-sizing-xxs;
  }

  .widget__header-control--active {
    background-color: variables.$vantage-base-primitives-color-tertiary-500;
    color: variables.$vantage-base-primitives-color-background-100;
  }

  .p-select {
    background: variables.$vantage-base-primitives-color-background-500;
    border-radius: variables.$vantage-base-sizing-xxs;
    height: 30px;

    .p-select-label {
      color: black;
    }
  }

  .widget__label {
    padding: 0;
  }
}

.widget__footer {
  bottom: 0;
  position: absolute;
  right: 0;
}

.widget__footer-controls {
  position: absolute;
  bottom: -4px;
  right: -8px;
}

.widget__subheader-controls {
  @include mixins.flex-center($horiz: flex-start, $gap: variables.$vantage-base-sizing-xxs);
  list-style-type: none;
  margin-left: auto;
  padding: 0;

  &.widget__subheader-controls__zones {
    margin-left: 0;

    .p-select {
      height: 30px;
      margin-left: variables.$vantage-base-sizing-xxs;
    }
  }

  li {
    @include sizing.padding('xxxs', 'xs');
    background: variables.$vantage-base-primitives-color-background-500;
    border: 1px solid variables.$vantage-base-primitives-color-border-100;
    border-radius: 2px;
    cursor: pointer;
  }

  .widget__header-control--active {
    background-color: variables.$vantage-base-primitives-color-blue-100;
    border-color: variables.$vantage-base-primitives-color-blue-200;
    color: variables.$vantage-base-primitives-color-tertiary-700;
  }

  .widget__label {
    padding: 0;
  }
}

.dwell-time__icon {
  @include mixins.flex-center();
  border: 1px solid variables.$vantage-base-primitives-color-border-200;
  border-radius: variables.$vantage-base-sizing-xxxs;
  height: 28px;
  margin: 0 auto;
  width: 28px;
}

.widget-message__yellow {
  background: variables.$vantage-base-primitives-color-secondary-100;
  border: 1px solid variables.$vantage-base-primitives-color-secondary-400;
  border-radius: variables.$vantage-base-sizing-xxxs;
  font-weight: normal;
  padding: 2px variables.$vantage-base-sizing-xxs;
  margin-top: variables.$vantage-base-sizing-xxxs;
  display: inline-block;
}

.widget p {
  max-width: 250px;
  text-align: center;
}

.dwell-time__container {
  @include sizing.padding('lg');
  background-color: variables.$vantage-base-primitives-color-background-400;
  border-radius: variables.$vantage-base-sizing-xxs;
  width: 100%;
}

.widget .dwell-time__time {
  @include mixins.flex-center($gap: variables.$vantage-base-sizing-xs);
  width: 100%;

  .widget__bignumber {
    @include typography.font-display($size: md, $weight: bold);
    flex-shrink: 0;
  }

  .widget__bignumber.divider {
    align-self: flex-start;
    line-height: variables.$vantage-base-sizing-xxl;
    width: variables.$vantage-base-sizing-xxs;
  }

  .widget__label {
    @include typography.font-body($size: xs, $weight: medium);
    padding: 0;
  }
}

.dwell__time-text {
  @include sizing.padding('xs');
  @include typography.font-body($size: sm, $weight: normal);
  text-align: center;
}

.ui-resizable-handle {
  background: none;
  opacity: 0;

  &::before,
  &::after {
    @include sizing.padding('xxxs');
    content: '';
    display: block;
    height: 24px;
    position: absolute;
    right: -4px;
    top: 4px;
    width: 24px;
  }

  &::before {
    background-color: variables.$vantage-base-primitives-color-tertiary-100;
    border: 1px solid variables.$vantage-base-primitives-color-tertiary-200;
    border-radius: 4px;
    transform: rotate(45deg);
    z-index: 0;
  }

  &::after {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="%23666" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" viewBox="0 0 20 20"><path d="m10 3 2 2H8l2-2v14l-2-2h4l-2 2"/></svg>');
    transform: rotate(-180deg);
  }
}

.widgets--edit .ui-resizable-handle {
  opacity: 1;
}

.grid-stack-item > .ui-resizable-se {
  // Important to overwrite the plugin doing these by ID
  bottom: 12px !important;
  height: 38px;
  opacity: 0;
  right: 10px !important;
  width: 38px;
}

.widget-edit-controls {
  @include mixins.flex-center($gap: variables.$vantage-base-sizing-xxxs);
  background: variables.$vantage-base-primitives-color-background-200;
  border: 1px solid variables.$vantage-base-primitives-color-tertiary-200;
  border-radius: 4px;
  color: variables.$vantage-base-primitives-color-tertiary-500;
  margin-right: 4px;
  padding: 4px;

  .widget-control {
    @include mixins.flex-center($gap: variables.$vantage-base-sizing-xxs);
    @include sizing.padding('xxs');
    @include mixins.rounded-border(
      $color: variables.$vantage-base-primitives-color-tertiary-200,
      $size: variables.$vantage-base-sizing-xxxs
    );
    margin: 0;
    position: relative;

    svg {
      margin: 0;
    }
  }

  .widget-control--move {
    cursor: move;
  }

  .widget-control--remove {
    padding: 0;

    .p-button {
      @include sizing.padding('xxs');
      border: none;
    }

    .p-button:hover {
      background-color: transparent;
      border: none;
    }
  }

  .widget-move,
  .widget-remove,
  .widget-resize {
    height: 38px;
    width: 38px;
  }
}

.apexcharts-menu-icon {
  background: url('/chart-download.png') center center;
  background-size: 20px;

  svg {
    display: none;
  }
}

.grid-stack > .grid-stack-item > .grid-stack-item-content {
  overflow: visible;

  .widget__body {
    overflow-x: auto;
    overflow-y: auto;
  }
}

.ui-resizable-handle:hover + .grid-stack-item-content,
.grid-stack-item-content:has(+ .ui-resizable-handle:hover) {
  .widget__footer-controls .p-tooltip-manual {
    display: inline-flex;
  }
}

.chart-wrapper {
  @include sizing.padding('sm', 0);
  height: calc(100% - 12px);
  position: relative;
  width: 100%;
}

.unique-total .chart-wrapper {
  height: calc(100% - 80px);
}

.p-tooltip-manual {
  opacity: 1.044;
  position: absolute;
  pointer-events: none;

  z-index: 1000;

  &.p-tooltip-left {
    width: fit-content;
    left: auto !important;
    right: 32px !important;
    top: 0px !important;

    .p-tooltip-text {
      @include sizing.padding('xs');
    }

    .p-tooltip-arrow {
      right: 0;
      top: 50%;
    }
  }

  &.p-tooltip-bottom {
    bottom: -73px !important;
    left: auto !important;
    min-width: 135px;
    right: -58px !important;
    width: fit-content;

    .p-tooltip-arrow {
      right: 50%;
      top: 0%;
    }
  }
}

.chart-wrapper:has(.apexcharts-toolbar:hover) {
  .p-tooltip-manual {
    display: inline-flex;
  }
}

.widget__header-title {
  @include mixins.flex-center();
  margin-right: variables.$vantage-base-sizing-xxxs;

  .premium-icon {
    fill: variables.$vantage-base-primitives-color-secondary-500;
  }
}

.widget__content-center {
  height: 100%;

  &.widget__content-center--flex {
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: variables.$vantage-base-sizing-xxs;
    height: 100%;
    width: 100%;
    justify-content: center;
  }
}

.apexcharts-toolbar:hover .apexcharts-menu {
  // Stolen from the .apexcharts-menu-open class that is normally applied on click
  opacity: 1;
  pointer-events: all;
  transition: 0.15s ease all;
}
</style>
